

import axios from "axios";
import { API_BASE_PATH } from "../environment";

const CREATE_LEAD_ENDPOINT = `${API_BASE_PATH}/lead/`;
const GET_COMPANIES_URL = `${API_BASE_PATH}/companies/`;

// Get auth headers from localStorage
export const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
  };
};

// ✅ Branch Interface
export interface Branch {
  branch_id: string;
  branch_name: string;
  branch_address: string;
  branch_city: string;
  branch_state: string;
  branch_zipcode: string;
  branch_country: string;
  gst_number: string;
}

// ✅ Contact Interface
export interface Contact {
  contact_id: string;
  company_name: string;
  company_mail: string;
  company_phone: string;
  company_remark: string;
  website_url: string | null;
  contact_person_name: string;
  branch_name: string;
  contact_person_desigination: string;
  created_at: string;
  branch: Branch[];
}

// ✅ Main Lead Interface
export interface Lead {
  lead_id: string;
  website_url: string | null;
  lead_description: string | null;
  lead_status: string;
  lead_value: string | null;
  Currency: string | null;
  contact: Contact | null;
  leadtype: {
    leadtype_id: string;
    leadtype_name: string;
  };
  leadsource: {
    LeadSource_id: string;
    LeadSource_name: string;
    Lead_status: string;
  };
  leadindustry: {
    LeadIndustry_id: string;
    LeadIndustry_name: string;
    Industry_status: string;
  };
  leadstage: {
    LeadStage_id: string;
    LeadStage_name: string;
    LeadStage_status: string;
  };
  users: {
    id: string;
    name: string;
  };
  client: {
    client_id: string;
  };
  products: {
    product_ids_raw: string;
    product_id: string;
    product_name: string;
    product_description: string;
    status: string;
    product_price: number;
    no_logins: number;
    rate_per_login: number;
    product_type_id?: string;
  };
  producttype?: {
    product_type_id: string;
    product_name: string;
  };
  customerPotential?: {
    customerPotential_id: string;
    customerPotential_name?: string;
    customerPotential_status?: string;
  };
  created_at: string;
  updated_at: string;
  last_viewed?: string;
}

// ✅ Form data used for Create/Update
export interface LeadFormData {
  website_url?: string | null;
  lead_description?: string | null;
  lead_status?: string | null;
  lead_value?: string | null;
  Currency?: string | null;
  leadtype_id?: string | null;
  leadsource_id?: string | null;
  leadindustry_id?: string | null;
  leadstage_id?: string | null;
  user_id?: string | null;
  product_id?: string | null;
  branch_id?: string | null;
  product_type_id?: string;
  customer_type_id?: string;
}

// ✅ Optional: Simplified Lead View
export interface LeadContactInfo {
  lead_id: string;
  lead_name: string;
  company_name: string;
  company_mail: string;
}

// ---------- Lead Payload Interface ----------
export interface CreateLeadPayload {
  leadindustry_id?: string;
  leadowner_id?: string;          // ✅ added
  leadsource_id?: string;
  leadtype_id?: string;
  product_id?: string;
  product_type_id?: string;
  customer_type_id?: string;
  customerPotential_id?: string;
  website_url?: string;
  lead_value?: string;
  Currency?: string;             // ✅ Ensure capitalization matches usage
  lead_status?: string;
  leadstage_id?: string;
  lead_description?: string;
  contact_id?: string;
  rate_per_login?: number;
  no_logins?: number;
  branch_id?: string;
  contact_name?: string;
  company_phone?: string;
  contact_Company_name?: string;
  contact_mail?: string;
  contact_phone?: string;
  leadgivenby_id?: string;   // ✅ added
  migrating_from?: string;
}

// ---------- Create Lead API ----------
export const createLead = async (payload: CreateLeadPayload) => {
  try {
    const response = await axios.post(
      CREATE_LEAD_ENDPOINT,
      new URLSearchParams(payload as any), // convert payload to x-www-form-urlencoded format
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          ...getAuthHeaders(), // <-- Inject auth headers here
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || "Failed to create lead");
  }
};

// ---------- Get Companies Interfaces ----------
export interface Branch {
  branch_id: string;
  branch_name: string;
  branch_address: string;
  branch_city: string;
  branch_state: string;
  branch_zipcode: string;
  branch_country: string;
  gst_number: string;
}

export interface Company {
  contact_id: string;
  company_name?: string;
  company_mail: string;
  company_phone: string;
  contact_remark: string;
  website_url: string | null;
  contact_person_name: string;
  contact_person_desigination: string;
  leadsource_id: string | null;
  client_id: string;
  created_at: string;
}

export interface CompanyWithBranches {
  company: Company;
  branches: Branch[];
}

export interface GetCompaniesResponse {
  data: never[];
  companies: CompanyWithBranches[];
}

// ---------- Get Companies API ----------
export const getCompanies = async (): Promise<GetCompaniesResponse> => {
  try {
    const response = await axios.get(GET_COMPANIES_URL, {
      headers: {
        ...getAuthHeaders(), // <-- Inject auth headers here
      },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch companies"
    );
  }
};



// ---------- Update Lead API ----------
export const updateLead = async (leadId: string, payload: CreateLeadPayload) => {
  try {
    const formPayload = new URLSearchParams();

    // Only append defined fields (avoid undefined/null breaking form submission)
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formPayload.append(key, String(value));
      } else {
        formPayload.append(key, ""); // Send empty value as required by FastAPI spec
      }
    });

    const response = await axios.put(
      `${API_BASE_PATH}/leads/${leadId}`,
      formPayload,
      {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      }
    );

    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || "Failed to update lead");
  }
};
